import type { CreateAddressInput, CreateCustomerInput, OrderFragment, OrderLineFragment, PaymentInput, SearchProductVariantResultFragment, ShippingMethodFragment, ShippingMethodQuoteFragment } from '#graphql-operations'
import type { OrderState } from '@layers/common/app/services/activeOrder'
import { defineQuery, useQuery } from '@pinia/colada'
import * as Sentry from '@sentry/nuxt'
import { defineStore } from 'pinia'
import { activeOrder as $activeOrder, addItemToOrder as $addItemToOrder, addPaymentToOrder as $addPaymentToOrder, adjustOrderLine as $adjustOrderLine, applyCouponCode as $applyCouponCode, nextOrderStates as $nextOrderStates, removeCouponCode as $removeCouponCode, removeOrderLine as $removeOrderLine, setCustomerForOrder as $setCustomerForOrder, setIntermediatePaymentMethod as $setIntermediatePaymentMethod, setOrderBillingAddress as $setOrderBillingAddress, setOrderShippingAddress as $setOrderShippingAddress, setOrderShippingMethod as $setOrderShippingMethod, transitionOrderToState as $transitionOrderToState } from '../services/activeOrder'

export const useActiveOrderQuery = defineQuery(() => {
  const { state, refetch, ...rest } = useQuery<OrderFragment | null>({
    key: ['activeOrder'],
    enabled: import.meta.client,
    query: () => $activeOrder(),
  })

  return { activeOrder: state, refetchActiveOrder: refetch, ...rest }
})

export const useActiveOrderStore = defineStore('activeOrder', () => {
  const queryCache = useQueryCache()
  const { activeOrder, refetchActiveOrder } = useActiveOrderQuery()

  const transitionOrderToState = async (nextState: OrderState) => {
    if (!activeOrder.value.data || activeOrder.value.data.state === nextState)
      return

    const nextOrderStates = await $nextOrderStates()

    if (!nextOrderStates.includes(nextState))
      throw new Error(`Failed to transition order to state ${nextState}, available states are: ${nextOrderStates.join(', ')}`)

    queryCache.setQueryData(['activeOrder'], await $transitionOrderToState(nextState))
  }

  const addItemToOrder = async (productVariant: SearchProductVariantResultFragment, quantity = 1) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $addItemToOrder(productVariant.id.toString(), quantity))
  }

  const removeOrderLine = async (line: OrderLineFragment) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $removeOrderLine(line.id.toString()))
  }

  const adjustOrderLine = async (line: OrderLineFragment, quantity: number) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $adjustOrderLine(line.id.toString(), quantity))
  }

  const setCustomerForOrder = async (input: CreateCustomerInput) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setCustomerForOrder(input))
    if (input.emailAddress)
      Sentry.setUser({ email: input.emailAddress })
  }

  const setOrderShippingMethod = async (shippingMethod: ShippingMethodQuoteFragment | ShippingMethodFragment) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setOrderShippingMethod(shippingMethod.id.toString()))
  }

  const setOrderShippingAddress = async (address: CreateAddressInput) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setOrderShippingAddress(address))
  }

  const setOrderBillingAddress = async (address: CreateAddressInput) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $setOrderBillingAddress(address))
  }

  const setIntermediatePaymentMethod = async (paymentMethodCode: string) => {
    if (activeOrder.value.data?.customFields?.intermediatePaymentMethod === paymentMethodCode)
      return
    queryCache.setQueryData(['activeOrder'], await $setIntermediatePaymentMethod(paymentMethodCode))
  }

  const applyCouponCode = async (couponCode: string) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $applyCouponCode(couponCode))
  }

  const removeCouponCode = async (couponCode: string) => {
    await transitionOrderToState('AddingItems')
    queryCache.setQueryData(['activeOrder'], await $removeCouponCode(couponCode))
  }

  const addPaymentToOrder = async (input: PaymentInput) => {
    await transitionOrderToState('ArrangingPayment')
    queryCache.setQueryData(['activeOrder'], await $addPaymentToOrder(input))
  }

  const orderSurcharges = computed(() => {
    return activeOrder.value.data?.surcharges?.filter(surcharge => surcharge.orderLineId == null) || []
  })

  const orderTotal = computed(() => {
    return activeOrder.value.data?.total || 0
  })

  const currency = computed(() => {
    return activeOrder.value.data?.currency || 'USD'
  })

  watch(() => activeOrder.value.data?.code, (orderCode) => {
    if (orderCode) {
      Sentry.setTag('orderCode', orderCode)
    }
  })

  return {
    activeOrder,
    refetchActiveOrder,
    orderSurcharges,
    orderTotal,
    currency,
    transitionOrderToState,
    addItemToOrder,
    removeOrderLine,
    adjustOrderLine,
    setCustomerForOrder,
    setOrderShippingMethod,
    setOrderShippingAddress,
    setOrderBillingAddress,
    setIntermediatePaymentMethod,
    applyCouponCode,
    removeCouponCode,
    addPaymentToOrder,
  }
})
